import { useCallback, useMemo, useRef, useState } from 'react';
import type { GridColDef } from '@mui/x-data-grid';

import {
    ApiWrapped,
    fetchInteractions,
    fetchInteractionsPqr,
    InteractionsPayload,
} from '../../../../../../../services/interactionsHistory.service';
import { generateOthersColumns } from '../shared/columns';

export type InteractionsFilters = {
    identification?: string;
    plate?: string;
    policy?: string;
    channelId?: string;
};

export type Endpoint = 'others' | 'pqr';

export type UseInteractionsDataArgs = {
    endpoint?: Endpoint;
    channel?: 'APP' | 'CHAT' | 'CHATBOT' | 'TELEFONÍA' | string;
    initialFilters?: InteractionsFilters;
    pageSize?: number;
    columnsFactory?: () => GridColDef[];
    autoload?: boolean;
};

export function useInteractionsData({
    endpoint = 'others',
    channel,
    initialFilters,
    pageSize = 20,
    columnsFactory,
    autoload = false,
}: UseInteractionsDataArgs) {
    if (endpoint === 'others' && !channel) {
        throw new Error(
            'useInteractionsData: "channel" is required when endpoint="others"'
        );
    }
    const [enabled, setEnabled] = useState(autoload);
    const [filters, setFilters] = useState<InteractionsFilters>(
        initialFilters ?? {}
    );
    const lastSubmittedRef = useRef<InteractionsFilters | null>(null);

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize,
    });
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<any[]>([]);
    const [rowCount, setRowCount] = useState(0);

    const columns = useMemo<GridColDef[]>(
        () => (columnsFactory ? columnsFactory() : generateOthersColumns()),
        [columnsFactory]
    );

    const normalize = (f: InteractionsFilters) => ({
        identification: f.identification?.trim() || undefined,
        plate: f.plate?.trim() || undefined,
        policy: f.policy?.trim() || undefined,
        channelId: f.channelId?.trim() || undefined,
    });

    const offset = paginationModel.page * paginationModel.pageSize;
    const limit = paginationModel.pageSize;

    const loadWith = useCallback(
        async (f: InteractionsFilters) => {
            setLoading(true);
            try {
                let res: ApiWrapped<InteractionsPayload>;
                const n = normalize(f);

                if (endpoint === 'pqr') {
                    res = await fetchInteractionsPqr({
                        identification: n.identification,
                        plate: n.plate,
                        policy: n.policy,
                        channelId: n.channelId,
                        offset,
                        limit,
                    });
                } else {
                    res = await fetchInteractions({
                        channel: channel!,
                        identification: n.identification,
                        plate: n.plate,
                        policy: n.policy,
                        offset,
                        limit,
                    });
                }

                const payload = res?.items;
                const data = payload?.data ?? [];
                const total = payload?.total ?? data.length;
                setRows(data);
                setRowCount(total);
            } catch (error) {
                console.error(error);
                setLoading(false);
            } finally {
                setLoading(false);
            }
        },
        [endpoint, channel, offset, limit]
    );

    const enable = useCallback(() => setEnabled(true), []);

    const onSubmitFilters = useCallback((newFilters: InteractionsFilters) => {
        setPaginationModel((p) => ({ ...p, page: 0 }));
        lastSubmittedRef.current = newFilters;
        setFilters({
            identification: newFilters.identification?.trim() || undefined,
            plate: newFilters.plate?.trim() || undefined,
            policy: newFilters.policy?.trim() || undefined,
            channelId: newFilters.channelId?.trim() || undefined,
        });
    }, []);

    const onResetFilters = useCallback(() => {
        setPaginationModel((p) => ({ ...p, page: 0 }));
        setFilters({
            identification: initialFilters?.identification?.trim() || undefined,
        });
    }, [initialFilters]);

    const search = useCallback(async () => {
        if (!enabled) setEnabled(true);
        const f = lastSubmittedRef.current ?? filters;
        await loadWith(f);
    }, [enabled, filters, loadWith]);

    return {
        rows,
        rowCount,
        loading,
        columns,
        paginationModel,
        setPaginationModel,
        filters,
        onSubmitFilters,
        onResetFilters,
        search,
        reload: () => loadWith(lastSubmittedRef.current ?? filters),
        enable,
    };
}
